<template>
    <div>
        <v-autocomplete
                v-model="carModels"
                :items="items"
                :label="label"
                :loading="loading"
                :search-input.sync="search"
                multiple
                chips
                deletable-chips
                full-width
                clearable
                no-filter
                item-value="id"
                item-text="text"
                color="primary"
                :placeholder="$t('camerasPage.searchCarHint')"
                :no-data-text="noDataText"
                prepend-icon="mdi-database-search"
        ></v-autocomplete>
        <div class="grey--text caption">{{ $t("camerasPage.multiCarHint") }}</div>
        <div v-if="failed" class="red--text">{{ $t("camerasPage.carsLoadFailed") }}</div>
    </div>
</template>

<script>
    // Car picker for the camera↔cars binding.
    //
    // A camera is bound to CAR RECORDS, plural: a `cars` row is a captain↔car
    // pairing, so one physical vehicle appears once per captain who drives it
    // (its registered owner and the person actually behind the wheel). The
    // dashcam is fitted to the vehicle, so it has to be tied to all of them —
    // binding one record only is what made a Safe Ride trip answer "no camera
    // for this ride" the moment the passenger pressed the button.
    //
    // Everything else mirrors ChooseCarSingle: the backend matches the plate,
    // the brand/model and the driver's name and phone, so a plate and a driver's
    // name both find the car without the picker asking which one was meant.
    const SEARCH_DEBOUNCE_MS = 300;
    const SEARCH_LIMIT = 50;

    export default {
        name: "choose-car-multi",
        props: {
            label: String,
            value: [Array, Number, String],
        },
        data() {
            return {
                loading: false,
                failed: false,
                searched: false,
                carModels: [],
                search: null,
                cars: [],
                // The cars currently chosen, kept apart from the search results:
                // the next search replaces `cars`, and an option that disappears
                // takes its chip's text with it.
                selectedCars: [],
                // Set for exactly one watcher run: filling the field with the
                // binding the camera already holds is not a re-bind, and must not
                // put car_ids into DataTable's changed-fields diff.
                initialising: false,
                // Once the admin has touched the field, the binding the dialog
                // opened with is history: a by-id resolve that lands late must
                // not paint those cars back over their choice.
                touched: false,
                // Monotonic request number: only the newest request may write to
                // `cars`. Without it a slow earlier search (or the mount resolve)
                // lands last and replaces a newer result set -- and a late failure
                // would wipe a good list.
                requestSeq: 0,
                debounce: null,
            }
        },
        computed: {
            items() {
                const options = this.cars.map(car => ({id: car.id, text: this.carLabel(car)}));
                this.selectedCars.forEach(car => {
                    if (!options.some(option => option.id === car.id))
                        options.unshift({id: car.id, text: this.carLabel(car)});
                });
                return options;
            },
            noDataText() {
                if (this.failed) return this.$t("camerasPage.carsLoadFailed");
                if (this.loading) return this.$t("camerasPage.carsSearching");
                return this.searched ? this.$t("camerasPage.noCarMatches") : this.$t("camerasPage.searchCarHint");
            },
        },
        methods: {
            carLabel(car) {
                const plate = car.plate_number || car.label || ("#" + car.id);
                return [plate, car.captain_name, car.captain_phone].filter(Boolean).join(" — ");
            },
            // The value may arrive as an array of ids, an array of car objects
            // (the show endpoint returns the `cars` relation) or a lone id from
            // the old single-car shape.
            idsFrom(value) {
                if (value === null || value === undefined || value === "") return [];
                const list = value instanceof Array ? value : [value];
                return list
                    .map(entry => (entry && typeof entry === "object") ? entry.id : entry)
                    .filter(id => id !== null && id !== undefined && id !== "")
                    .map(Number);
            },
            fetchCars(params, onLoaded) {
                const seq = ++this.requestSeq;
                this.loading = true;
                this.failed = false;
                this.axios.get(this.api.ACTIONS.CARS, {params})
                    .then(({data}) => {
                        if (seq !== this.requestSeq) return;
                        const cars = (data && data.data) || [];
                        this.cars = cars instanceof Array ? cars : [cars].filter(Boolean);
                        if (onLoaded) onLoaded(this.cars);
                    })
                    .catch(error => {
                        if (seq !== this.requestSeq) return;
                        // A swallowed failure is indistinguishable from an empty
                        // fleet: the admin reads "no data" as "the car is not in
                        // the system" and goes looking in the wrong place.
                        this.cars = [];
                        this.failed = true;
                        const message = error && error.response && error.response.data && error.response.data.message;
                        window.getApp.$emit("SHOW_TOAST", message || this.$t("camerasPage.carsLoadFailed"), "red");
                    })
                    .finally(() => {
                        if (seq === this.requestSeq) this.loading = false;
                    });
            },
        },
        watch: {
            search(val) {
                if (!val) return;
                // v-autocomplete writes a picked item's own text back into
                // search-input; searching for it again would be pointless.
                if (this.items.some(item => item.text === val)) return;
                clearTimeout(this.debounce);
                this.debounce = setTimeout(() => {
                    this.searched = true;
                    this.fetchCars({search: val, limit: SEARCH_LIMIT});
                }, SEARCH_DEBOUNCE_MS);
            },
            carModels(val) {
                const ids = this.idsFrom(val);
                // Keep a label for every chosen car even after the search list
                // that produced it has been replaced.
                this.selectedCars = ids.map(id =>
                    this.cars.find(car => Number(car.id) === id)
                    || this.selectedCars.find(car => Number(car.id) === id)
                ).filter(Boolean);

                if (this.initialising) {
                    this.initialising = false;
                    return;
                }
                this.touched = true;
                // Empty string, not an empty array: DataTable submits edits as
                // FormData and serialises arrays by index, so an EMPTY array
                // appends nothing and the key vanishes from the body — which
                // the backend reads as "field untouched, leave the bindings
                // alone". Clearing every chip would then save nothing and
                // still show a green toast. ChooseCarSingle hit the same wall.
                this.$emit('input', ids.length ? ids : '');
            },
        },
        mounted() {
            const ids = this.idsFrom(this.value);
            if (!ids.length) return;
            // Resolve the existing bindings by id: the fleet is longer than one
            // page of search results, so they cannot be assumed to be in a list.
            // The cars endpoint takes one id at a time, so this is one call each
            // — a camera is bound to a handful of records, never a fleet.
            Promise.all(ids.map(id => new Promise(resolve => {
                this.axios.get(this.api.ACTIONS.CARS, {params: {id}})
                    .then(({data}) => {
                        const rows = (data && data.data) || [];
                        resolve((rows instanceof Array ? rows : [rows])[0] || null);
                    })
                    .catch(() => resolve(null));
            }))).then(cars => {
                // Clearing the field is a choice too, and it emits before this
                // can land -- painting the old cars back would show one set and
                // save another.
                if (this.touched) return;
                const resolved = cars.filter(Boolean);
                if (!resolved.length) return;
                this.selectedCars = resolved;
                this.initialising = true;
                this.carModels = resolved.map(car => car.id);
            });
        },
        beforeDestroy() {
            clearTimeout(this.debounce);
        },
    }
</script>
